import React, { useEffect, useMemo, useState, useCallback } from "react";
import YouLaberIDEShell from "./YouLaberIDEShell";
import { DeviceGrid, PasswordCard } from "./YouLaberShared";
import { getDeviceTypeMeta } from "../../config/ccDeviceTypes";
import {
  InstallInviteCard,
  InstallInstructionModal,
} from "./YouLaberInstallEntry";
import { YouLabberLocalDownloadBanner } from "./YouLabberLocalDownloadEntry";

/**
 * YouLaberPanel —— 顶部 tab "YouLaber 实验员" 的主面板
 *
 * 三态：
 *   L1  类别卡片网格（按 device_type 聚合）
 *   L2  选中类别后的实验员卡片网格 + 选中后的密码卡
 *   L3  接入完成 → 直接复用 ccma 的 <CCPanel /> 聊天
 *
 * 不与"综合训练"耦合：state 由 useCCState 全局持有，切走切回都不丢；
 * "在 ccma 输入 'YouLaber...'" 的旧路径也并存（弹 CCDeviceSelector 兜底）。
 */
const YouLaberPanel = (props) => {
  const {
    apiUrl,                       // CCMA_API_URL
    // 来自 useCCState 的全部 props（透传给 CCPanel/CCControlBar/CCFileTree）
    ccSelectedDevice,
    ccMessages,
    ccStreamingText,
    ccThinkingText,
    ccSessionState,
    ccStatus,
    ccInitInfo,
    ccPendingQuestion,
    ccTaskInfo,
    ccTokenUsage,
    ccFileTree,
    ccRoster,
    devicePasswordError,
    onSendMessage,
    onInterrupt,
    onQuestionRespond,
    onSelectDevice,               // (device) => void  通知 App，会发 cc_select_device ws
    onDisconnect,                 // () => void        清空 ccSelectedDevice，回到 L1
    onSendControl,                // (payload) => void
    onCompact,
    onClearHistory,
    onExportChatlog,
  } = props;

  const baseUrl = (apiUrl || "").replace(/\/$/, "");

  // 路由态：null = L1 类别页；string = 进入了某 device_type
  const [selectedType, setSelectedType] = useState(null);
  // L2 选中设备进入密码确认
  const [pendingDevice, setPendingDevice] = useState(null);
  const [pwdError, setPwdError] = useState("");

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [installGuideOpen, setInstallGuideOpen] = useState(false);

  // 已接入设备时直接进 L3，不必停在 L2
  const inChat = !!ccSelectedDevice;

  useEffect(() => {
    if (!ccSelectedDevice) return;
    setPendingDevice(null);
    setPwdError("");
  }, [ccSelectedDevice]);

  useEffect(() => {
    if (pendingDevice && devicePasswordError) {
      setPwdError(devicePasswordError);
    }
  }, [devicePasswordError, pendingDevice]);

  const fetchDevices = useCallback(async () => {
    if (!baseUrl) return;
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${baseUrl}/api/claw/cc-devices`);
      const data = await res.json();
      setDevices(Array.isArray(data) ? data : (data.devices || []));
    } catch (e) {
      setError(`无法加载实验员列表：${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  // 进入 L1 / L2 时拉一次 + 30s 轮询；进入 L3 后停止轮询
  useEffect(() => {
    if (inChat) return;
    fetchDevices();
    const t = setInterval(fetchDevices, 30000);
    return () => clearInterval(t);
  }, [inChat, fetchDevices]);

  // L1：按 device_type 聚合（device_type 由 bridge 上报，前端不再做白名单/排序优先级）
  // 排序规则：可用实验员数（online）多的类别排前，其次按 device_type 中文/字母序。
  const grouped = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => {
      const key = d.device_type || "未分类";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    const all = Array.from(map.entries()).map(([type, list]) => ({
      type,
      list,
      onlineN: list.filter((d) => d.status === "online").length,
    }));
    all.sort((a, b) => {
      if (b.onlineN !== a.onlineN) return b.onlineN - a.onlineN;
      return a.type.localeCompare(b.type, "zh-CN");
    });
    return all.map(({ type, list }) => ({ type, list }));
  }, [devices]);

  // ========== 渲染 ==========

  // ---- L3 直通 CC 聊天 ----
  if (inChat) {
    return (
      <YouLaberIDEShell
        apiUrl={apiUrl}
        header={(
          <YouLaberSessionBar
            device={ccSelectedDevice}
            onChange={() => { setSelectedType(ccSelectedDevice.device_type || null); onDisconnect && onDisconnect(); }}
            onDisconnect={onDisconnect}
          />
        )}
        ccSelectedDevice={ccSelectedDevice}
        ccMessages={ccMessages}
        ccStreamingText={ccStreamingText}
        ccThinkingText={ccThinkingText}
        ccSessionState={ccSessionState}
        ccStatus={ccStatus}
        ccInitInfo={ccInitInfo}
        ccPendingQuestion={ccPendingQuestion}
        ccTaskInfo={ccTaskInfo}
        ccTokenUsage={ccTokenUsage}
        ccFileTree={ccFileTree}
        ccRoster={ccRoster}
        onSendMessage={onSendMessage}
        onInterrupt={onInterrupt}
        onQuestionRespond={onQuestionRespond}
        onSendControl={onSendControl}
        onCompact={onCompact}
        onClearHistory={onClearHistory}
        onExportChatlog={onExportChatlog}
      />
    );
  }

  // ---- L2 密码卡 ----
  if (pendingDevice) {
    return (
      <ScrollShell>
        <Header title={`接入 ${pendingDevice.device_name}`} subtitle="请输入实验员访问密码完成接入" />
        <div style={{ marginTop: "32px" }}>
          <PasswordCard
            device={pendingDevice}
            error={pwdError}
            onCancel={() => { setPendingDevice(null); setPwdError(""); }}
            onConfirm={(pwd) => {
              setPwdError("");
              onSelectDevice && onSelectDevice({
                device_id: pendingDevice.device_id,
                device_name: pendingDevice.device_name,
                device_type: pendingDevice.device_type,
                device_password: pwd,
              });
            }}
          />
        </div>
      </ScrollShell>
    );
  }

  // ---- L2 设备网格 ----
  if (selectedType) {
    const meta = getDeviceTypeMeta(selectedType);
    const list = (grouped.find((g) => g.type === selectedType) || { list: [] }).list;
    return (
      <ScrollShell>
        <BackBar onBack={() => setSelectedType(null)} text="所有实验员类别" />
        <Header
          title={<><span style={{ marginRight: 8 }}>{meta.icon}</span>{selectedType}</>}
          subtitle="选择一位空闲的实验员开始实验。"
          accent={meta.accent}
        />
        <div style={{ marginTop: "20px" }}>
          {loading && devices.length === 0 ? (
            <SkeletonGrid />
          ) : (
            <DeviceGrid
              devices={list}
              onSelect={(d) => {
                if (d.has_password) { setPendingDevice(d); setPwdError(""); }
                else onSelectDevice && onSelectDevice({
                  device_id: d.device_id, device_name: d.device_name,
                  device_type: d.device_type, device_password: "",
                });
              }}
              emptyHint={`暂无可用的「${selectedType}」，请等待教师/同学部署。`}
            />
          )}
        </div>
        {error && <div style={{ color: "#f85149", marginTop: 16, fontSize: 12 }}>{error}</div>}
      </ScrollShell>
    );
  }

  // ---- L1 课程类别卡片 ----
  return (
    <ScrollShell>
      <Header
        title="YouLabber 智能实验员"
        subtitle="选择一个课程方向，进入对应的实验员清单。每位智能实验员都是一台运行 YouLabber 的真实电脑，与你 1:1 协作完成实验任务。"
      />
      <YouLabberLocalDownloadBanner />
      <div style={{
        marginTop: "24px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: "14px",
      }}>
        {grouped.length === 0 && !loading && (
          <div style={{ color: "#6e7681", fontSize: "13px", padding: "32px", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: "10px", gridColumn: "1 / -1", textAlign: "center" }}>
            当前没有可用的实验员。请联系教师或检查 ybg-cc-bridge 是否已上线。
          </div>
        )}
        {grouped.map(({ type, list }) => {
          const meta = getDeviceTypeMeta(type);
          const onlineN = list.filter((d) => d.status === "online").length;
          const busyN = list.filter((d) => d.status === "busy").length;
          const offlineN = list.filter((d) => d.status === "offline").length;
          return (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "flex", flexDirection: "column", gap: "14px",
                padding: "20px",
                borderRadius: "14px",
                background: `linear-gradient(160deg, ${meta.accent}15 0%, rgba(13,17,23,0.92) 70%)`,
                border: `1px solid ${meta.accent}35`,
                boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.35)",
                transition: "transform 0.15s ease, border-color 0.15s ease",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = meta.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = `${meta.accent}35`; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "48px", height: "48px", borderRadius: "12px",
                  background: `${meta.accent}25`, color: meta.accent,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "26px",
                  border: `1px solid ${meta.accent}55`,
                }}>{meta.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: "#e6edf3", fontSize: "16px", fontWeight: 600,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{type}</div>
                  <div style={{ color: meta.accent, fontSize: "11px", marginTop: "2px" }}>
                    共 {list.length} 台 · {onlineN} 台空闲
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "auto" }}>
                <Stat label="空闲" n={onlineN} color="#3fb950" />
                <Stat label="忙碌" n={busyN} color="#f0883e" />
                <Stat label="离线" n={offlineN} color="#6e7681" />
              </div>
              <div style={{ color: meta.accent, fontSize: "12px", fontWeight: 600, marginTop: "2px" }}>
                查看实验员清单 →
              </div>
            </button>
          );
        })}
        <InstallInviteCard onClick={() => setInstallGuideOpen(true)} />
      </div>
      {error && <div style={{ color: "#f85149", marginTop: 16, fontSize: 12 }}>{error}</div>}
      <InstallInstructionModal
        open={installGuideOpen}
        onClose={() => setInstallGuideOpen(false)}
      />
    </ScrollShell>
  );
};

// ─── 局部小组件 ──────────────────────────────────────────────────────

const ScrollShell = ({ children }) => (
  <div style={{
    flex: 1, minHeight: 0, width: "100%", overflow: "auto",
    background: "radial-gradient(ellipse at top, rgba(88,166,255,0.06), transparent 60%), #0a0d12",
    color: "#e6edf3",
  }}>
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 28px 48px" }}>
      {children}
    </div>
  </div>
);

const Header = ({ title, subtitle, accent }) => (
  <div>
    <h2 style={{
      margin: 0, fontSize: "22px", fontWeight: 700,
      color: "#e6edf3", letterSpacing: "0.2px",
      borderLeft: accent ? `3px solid ${accent}` : "3px solid #58a6ff",
      paddingLeft: "12px",
    }}>{title}</h2>
    {subtitle && (
      <p style={{ margin: "10px 0 0", color: "#8b949e", fontSize: "13px", lineHeight: 1.6, paddingLeft: "15px" }}>
        {subtitle}
      </p>
    )}
  </div>
);

const BackBar = ({ onBack, text }) => (
  <button
    onClick={onBack}
    style={{
      background: "transparent", border: "none", color: "#8b949e",
      cursor: "pointer", fontSize: "12px", padding: 0, marginBottom: "16px",
      display: "inline-flex", alignItems: "center", gap: "4px", fontFamily: "inherit",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = "#58a6ff")}
    onMouseLeave={(e) => (e.currentTarget.style.color = "#8b949e")}
  >
    ← 返回{text}
  </button>
);

const Stat = ({ label, n, color }) => (
  <div style={{
    display: "flex", alignItems: "baseline", gap: "5px",
    background: "rgba(255,255,255,0.03)",
    padding: "4px 10px", borderRadius: "6px",
    border: "1px solid rgba(255,255,255,0.06)",
  }}>
    <span style={{ color, fontSize: "16px", fontWeight: 700, fontFamily: "'Courier New', monospace" }}>{n}</span>
    <span style={{ color: "#6e7681", fontSize: "10px" }}>{label}</span>
  </div>
);

const SkeletonGrid = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "12px" }}>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} style={{
        height: "140px", borderRadius: "12px",
        background: "linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
        backgroundSize: "200% 100%",
        animation: "youlaberShimmer 1.4s linear infinite",
        border: "1px solid rgba(255,255,255,0.05)",
      }} />
    ))}
    <style>{`@keyframes youlaberShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
  </div>
);

/** L3 顶部一行：已接入 / 更换 / 断开 */
const YouLaberSessionBar = ({ device, onChange, onDisconnect }) => {
  const meta = getDeviceTypeMeta(device?.device_type);
  const showDeviceType = device?.device_type && device.device_type !== "cc-bridge";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "8px 16px",
      background: "linear-gradient(90deg, rgba(88,166,255,0.08), transparent 60%)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      flexShrink: 0, fontSize: "12px",
    }}>
      <span style={{ color: meta.accent }}>{meta.icon}</span>
      <span style={{ color: "#9aa4b2" }}>已接入</span>
      <span style={{ color: "#e6edf3", fontWeight: 600 }}>{device?.device_name}</span>
      {showDeviceType && (
        <>
          <span style={{ color: "#484f58" }}>·</span>
          <span style={{ color: meta.accent, fontSize: "11px" }}>{device.device_type}</span>
        </>
      )}
      <span style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
        <button onClick={onChange} style={{
          background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
          color: "#9aa4b2", padding: "3px 10px", borderRadius: "5px",
          fontSize: "11px", cursor: "pointer", fontFamily: "inherit",
        }}>更换实验员</button>
        <button onClick={onDisconnect} style={{
          background: "transparent", border: "1px solid rgba(248,81,73,0.4)",
          color: "#f85149", padding: "3px 10px", borderRadius: "5px",
          fontSize: "11px", cursor: "pointer", fontFamily: "inherit",
        }}>断开</button>
      </span>
    </div>
  );
};

export default YouLaberPanel;
